import { useCallback, useEffect } from 'react';
import {
  getProjectIdByName as getSpaceIdByName,
  getProjectIdByPath as getSpaceIdByPath,
  createDocument,
  createChat,
  createVoiceChannel,
  createSpreadsheet,
  createPresentation,
  createFolder,
  createList,
  renameSpace,
  moveSpace,
  archiveSpace,
  duplicateSpace,
  getAllProjects as getAllSpaces
} from '@/db/projects';
import { useAppStore } from '@/store/appStore';
import type { ContextMenuAction } from '@/components/layout/sidebar/ProjectContextMenu';
import type { ItemType } from '@/store/appStore';
import type { Document, Chat, VoiceChannel, Spreadsheet, Presentation } from '@/types';
import { useItemNavigation } from './useItemNavigation';
import { clearStructureCache } from '@/utils/structureCache';
import { organizeProjectsFromDatabase, fetchCategoriesFromDatabase, organizeCategories } from '@/utils/organizeProjects';
import { toast } from '@/utils/toast';
import { isUUID } from '@/utils/validation';
import { logger } from '@/utils/logger';
import { useOrganization } from '@/context/OrganizationContext';
import { useDialogStore } from '@/store/dialogStore';

/**
* Hook per gestire le azioni del context menu nella sidebar.
* Gestisce:
* - Apertura delle impostazioni (space, folder, list)
* - Creazione di nuovi item (documenti, chat, voice channels, spreadsheet, presentazioni)
* 
* @returns Oggetto con funzione per gestire le azioni del context menu
* @returns handleContextMenuAction - Funzione per eseguire un'azione del context menu
*/
export function useContextMenuActions() {
  const setSelectedProject = useAppStore((state) => state.setSelectedProject);
  const setSelectedFolder = useAppStore((state) => state.setSelectedFolder);
  const setSelectedList = useAppStore((state) => state.setSelectedList);
  const openSpaceSettings = useAppStore((state) => state.openSpaceSettings);
  const openListSettings = useAppStore((state) => state.openListSettings);
  const setCurrentItem = useAppStore((state) => state.setCurrentItem);
  const setFilters = useAppStore((state) => state.setFilters);
  const filters = useAppStore((state) => state.filters);
  const setSpaces = useAppStore((state) => state.setSpaces);
  const setCategories = useAppStore((state) => state.setCategories);
  const removeSpaceItems = useAppStore((state) => state.removeSpaceItems);
  const spaces = useAppStore((state) => state.spaces);
  const folderMappings = useAppStore((state) => state.folderMappings);
  const addItemOptimistic = useAppStore((state) => state.addItemOptimistic);
  const updateFolderMappingOptimistic = useAppStore((state) => state.updateFolderMappingOptimistic);
  const setEditingItem = useAppStore((state) => state.setEditingItem);
  const setPendingItemCreation = useAppStore((state) => state.setPendingItemCreation);
  const { refreshSpaceItems } = useItemNavigation();
  const { currentOrg } = useOrganization();
  const currentOrgId = currentOrg?.id;
  const dialogConfirm = useDialogStore((state) => state.confirm);
  const dialogPrompt = useDialogStore((state) => state.prompt);

  const refreshAll = useCallback(async () => {
    await clearStructureCache();
    const [refreshedSpaces, dbCategories] = await Promise.all([
      organizeProjectsFromDatabase(undefined, { forceRefresh: true, orgId: currentOrgId }),
      fetchCategoriesFromDatabase(currentOrgId),
    ]);
    setSpaces(refreshedSpaces);
    if (dbCategories.length > 0) {
      setCategories(organizeCategories(dbCategories, refreshedSpaces));
    } else {
      setCategories([]);
    }
    return refreshedSpaces;
  }, [currentOrgId, setSpaces, setCategories]);

  const handleContextMenuAction = useCallback(async (action: ContextMenuAction) => {
    if (action.type === 'settings') {
      // Get space ID
      const spaceId = await getSpaceIdByName(action.spaceName);
      setSelectedProject(action.spaceName, spaceId || undefined);

      if (action.listName) {
        // Open list/folder settings
        setSelectedFolder(action.folderName || null, undefined);
        setSelectedList(action.listName, undefined);

        // Get folder and list IDs using path
        if (spaceId) {
          if (action.folderName && action.listName) {
            const folderPath = `${action.spaceName}/${action.folderName}`;
            const listPath = `${folderPath}/${action.listName}`;
            const folderId = await getSpaceIdByPath(folderPath);
            const listId = await getSpaceIdByPath(listPath);
            setSelectedFolder(action.folderName, folderId || undefined);
            setSelectedList(action.listName, listId || undefined);
          } else if (action.listName) {
            const listPath = `${action.spaceName}/${action.listName}`;
            const listId = await getSpaceIdByPath(listPath);
            setSelectedList(action.listName, listId || undefined);
          } else if (action.folderName) {
            const folderPath = `${action.spaceName}/${action.folderName}`;
            const folderId = await getSpaceIdByPath(folderPath);
            setSelectedFolder(action.folderName, folderId || undefined);
          }
        }
        openListSettings();
      } else if (action.folderName) {
        // Open folder settings
        setSelectedFolder(action.folderName, undefined);
        setSelectedList('', undefined);

        // Get folder ID using path
        if (spaceId) {
          const folderPath = `${action.spaceName}/${action.folderName}`;
          const folderId = await getSpaceIdByPath(folderPath);
          setSelectedFolder(action.folderName, folderId || undefined);
        }
        openListSettings();
      } else {
        // Open space settings — keep selectedProject, clear only folder/list
        setSelectedFolder(null, undefined);
        setSelectedList('', undefined);
        openSpaceSettings();
      }
    } else if (action.type === 'add-folder' || action.type === 'add-list') {
      // Unified handler for folders and lists
      try {
        // UNIFIED: Handle both name-based paths and direct IDs
        let containerId: string | null = null;

        // If folderName or listName looks like an ID (UUID format), use it directly

        if (action.listName && isUUID(action.listName)) {
          // Direct ID provided
          containerId = action.listName;
        } else if (action.folderName && isUUID(action.folderName)) {
          // Direct ID provided
          containerId = action.folderName;
        } else {
          // Try path resolution
          const path = action.type === 'add-folder'
            ? (action.listName
              ? (action.folderName ? `${action.spaceName}/${action.folderName}/${action.listName}` : `${action.spaceName}/${action.listName}`)
              : (action.folderName ? `${action.spaceName}/${action.folderName}` : action.spaceName))
            : (action.folderName ? `${action.spaceName}/${action.folderName}` : action.spaceName);

          containerId = await getSpaceIdByPath(path);

          // If path resolution failed, try to get space ID and use folderMappings
          if (!containerId && action.folderName) {
            const spaceId = await getSpaceIdByName(action.spaceName);
            if (spaceId && folderMappings?.[action.spaceName]?.[action.folderName]) {
              containerId = folderMappings[action.spaceName][action.folderName];
            }
          }
        }

        if (!containerId) {
          const path = action.type === 'add-folder'
            ? (action.listName
              ? (action.folderName ? `${action.spaceName}/${action.folderName}/${action.listName}` : `${action.spaceName}/${action.listName}`)
              : (action.folderName ? `${action.spaceName}/${action.folderName}` : action.spaceName))
            : (action.folderName ? `${action.spaceName}/${action.folderName}` : action.spaceName);
          throw new Error(`Container not found for path: ${path}`);
        }

        // UNIFIED: Create temporary item with empty name in editing mode (inline editing)
        const itemType = action.type === 'add-folder' ? 'folder' : 'list';
        const tempItemId = `temp-${Date.now()}`;

        // Determine folderId for lists
        let folderIdForList: string | null = null;
        if (itemType === 'list') {
          folderIdForList = action.folderName
            ? (folderMappings?.[action.spaceName]?.[action.folderName] || containerId)
            : null;
        }

        // Optimistic update: add item immediately to UI with empty name
        addItemOptimistic({
          spaceName: action.spaceName,
          itemId: tempItemId,
          itemName: '', // Empty name - will be filled via inline editing
          itemType: itemType,
          parentId: containerId,
          folderId: folderIdForList,
        });

        // Set item in editing mode
        setEditingItem(tempItemId, true);

        // Save creation info in store for later use when saving name
        setPendingItemCreation(tempItemId, {
          spaceName: action.spaceName,
          itemType: itemType,
          containerId: containerId,
          folderId: folderIdForList,
          folderName: action.folderName || null,
        });
      } catch (error) {
        logger.error(`Error creating ${action.type === 'add-folder' ? 'folder' : 'list'}:`, error);
        toast(
          `Errore nella creazione della ${action.type === 'add-folder' ? 'cartella' : 'lista'}: ${error instanceof Error ? error.message : 'Errore sconosciuto'}`,
          'error'
        );
      }
    } else if (action.type === 'rename') {
      // Rename item
      try {
        let itemId: string | null = null;
        let currentName: string;
        let path = '';

        if (action.itemId) {
          // Direct ID provided (for generic items like documents, chats, etc.)
          itemId = action.itemId;
          currentName = (() => {
            // Find item name from projectItems in store
            const allItems = useAppStore.getState().projectItems;
            const spaceItems = allItems[action.spaceName] || [];
            const found = spaceItems.find(i => i.id === action.itemId);
            return found?.name || 'Elemento';
          })();
        } else {
          if (action.listName) {
            path = action.folderName
              ? `${action.spaceName}/${action.folderName}/${action.listName}`
              : `${action.spaceName}/${action.listName}`;
          } else if (action.folderName) {
            path = `${action.spaceName}/${action.folderName}`;
          } else {
            path = action.spaceName;
          }

          itemId = await getSpaceIdByPath(path);
          currentName = action.listName || action.folderName || action.spaceName;
        }

        if (!itemId) throw new Error(`Item not found`);
        const newName = await dialogPrompt({ title: 'Rinomina', message: 'Inserisci il nuovo nome:', defaultValue: currentName });
        if (!newName || !newName.trim() || newName.trim() === currentName) return;

        await renameSpace(itemId, newName.trim());
        await refreshSpaceItems(action.spaceName);

        // With ID-based paths, rename does NOT require updating filters.path
        // since path segments are IDs not names
      } catch (error) {
        logger.error('Error renaming item:', error);
        toast(
          `Errore nella rinomina: ${error instanceof Error ? error.message : 'Errore sconosciuto'}`,
          'error'
        );
      }
    } else if (action.type === 'delete') {
      // Delete item (use archive to recursively delete children)
      try {
        let itemId: string | null = null;
        let path = '';

        if (action.itemId) {
          itemId = action.itemId;
          path = action.spaceName; // For close-item detection we just use spaceName
        } else {
          if (action.listName) {
            path = action.folderName
              ? `${action.spaceName}/${action.folderName}/${action.listName}`
              : `${action.spaceName}/${action.listName}`;
          } else if (action.folderName) {
            path = `${action.spaceName}/${action.folderName}`;
          } else {
            path = action.spaceName;
          }

          itemId = await getSpaceIdByPath(path);
        }
        if (!itemId) throw new Error(`Item not found for path: ${path}`);

        const itemName = action.itemId
          ? (() => {
              const allItems = useAppStore.getState().projectItems;
              const spaceItems = allItems[action.spaceName] || [];
              return spaceItems.find(i => i.id === action.itemId)?.name || 'Elemento';
            })()
          : (action.listName || action.folderName || action.spaceName);
        const confirmed = await dialogConfirm({ title: 'Elimina', message: `Sei sicuro di voler eliminare "${itemName}"?`, confirmLabel: 'Elimina', variant: 'danger' });
        if (!confirmed) return;

        // Check if we're deleting a space (not a folder, list, or generic item)
        const isDeletingGenericItem = !!action.itemId;
        const isDeletingSpace = !action.listName && !action.folderName && !isDeletingGenericItem;
        const deletedSpaceName = isDeletingSpace ? action.spaceName : null;

        // Use archiveSpace which recursively deletes children
        await archiveSpace(itemId);

        // Clear structure cache to remove deleted space from cache
        try {
          await clearStructureCache();
        } catch (cacheError) {
          logger.error('Error clearing structure cache:', cacheError);
        }

        // If we deleted a space, remove its items and mappings from store immediately
        // and close any open items that belong to this space
        if (isDeletingSpace && deletedSpaceName) {
          removeSpaceItems(deletedSpaceName);

          // Close any open items that belong to the deleted space
          const currentPath = filters.path;
          if (currentPath === deletedSpaceName || currentPath?.startsWith(deletedSpaceName + '/')) {
            const closeAllItems = useAppStore.getState().closeAllItems;
            closeAllItems();
          }
        }

        // Force refresh spaces and categories in store to remove deleted space
        // This will trigger SpacesList to reload children from Supabase
        try {
          await refreshAll();

          // Also clear folderMappings to force reload
          // This ensures SpacesList will reload children even if spaces structure looks similar
          const setFolderMappings = useAppStore.getState().setFolderMappings;
          setFolderMappings({});
        } catch (refreshError) {
          logger.error('Error refreshing spaces after deletion:', refreshError);
        }

        // If we deleted a generic item (doc, chat, etc.), close it if currently open and refresh
        if (isDeletingGenericItem && action.itemId) {
          const state = useAppStore.getState();
          const currentItem = state.currentDocument || state.currentChat || state.currentVoiceChannel || state.currentSpreadsheet || state.currentPresentation;
          if (currentItem && 'id' in currentItem && currentItem.id === action.itemId) {
            state.closeAllItems();
          }
        }

        // Only refresh space items if we didn't delete the entire space
        // (if we deleted a space, it no longer exists, so refreshSpaceItems would fail silently)
        if (!isDeletingSpace) {
          await refreshSpaceItems(action.spaceName);
        }

        // Clear filters if we deleted the current item and redirect to projects view
        const currentPath = filters.path;
        if (currentPath === path || currentPath?.startsWith(path + '/')) {
          // Redirect to projects view instead of setting path to null
          setFilters({ path: null });
          // Change view mode to projects to avoid routing issues
          const setViewMode = useAppStore.getState().setViewMode;
          setViewMode('projects');
        }
      } catch (error) {
        logger.error('Error deleting item:', error);
        toast(
          `Errore nell'eliminazione: ${error instanceof Error ? error.message : 'Errore sconosciuto'}`,
          'error'
        );
      }
    } else if (action.type === 'move') {
      // Move item to another space
      try {
        let itemId: string | null = null;
        let path = '';

        if (action.itemId) {
          itemId = action.itemId;
        } else {
          if (action.listName) {
            path = action.folderName
              ? `${action.spaceName}/${action.folderName}/${action.listName}`
              : `${action.spaceName}/${action.listName}`;
          } else if (action.folderName) {
            path = `${action.spaceName}/${action.folderName}`;
          } else {
            path = action.spaceName;
          }

          itemId = await getSpaceIdByPath(path);
        }
        if (!itemId) throw new Error(`Item not found`);

        // Get all spaces for selection
        const spaces = await getAllSpaces();
        const spaceNames = spaces.map(s => s.name).join('\n');
        const targetSpaceName = await dialogPrompt({ title: 'Sposta', message: `Sposta in quale spazio?\n\nSpazi disponibili:\n${spaceNames}`, placeholder: 'Nome dello spazio' });
        if (!targetSpaceName || !targetSpaceName.trim()) return;

        const targetSpaceId = await getSpaceIdByName(targetSpaceName.trim());
        if (!targetSpaceId) throw new Error(`Space ${targetSpaceName} not found`);

        await moveSpace(itemId, targetSpaceId);
        await refreshSpaceItems(action.spaceName);
        await refreshSpaceItems(targetSpaceName.trim());

        // Update filters if we moved the current item
        // With ID-based paths, the item's ID stays the same; the parent path changes
        if (path) {
          const currentPath = filters.path;
          if (currentPath === path || currentPath?.startsWith(path + '/')) {
            // After move, the item is under the target space with same ID
            setFilters({ path: `${targetSpaceId}/${itemId}` });
          }
        }
      } catch (error) {
        logger.error('Error moving item:', error);
        toast(
          `Errore nello spostamento: ${error instanceof Error ? error.message : 'Errore sconosciuto'}`,
          'error'
        );
      }
    } else if (action.type === 'move-to-folder') {
      // Move item into a folder within same space
      try {
        let path = '';
        if (action.listName) {
          path = action.folderName
            ? `${action.spaceName}/${action.folderName}/${action.listName}`
            : `${action.spaceName}/${action.listName}`;
        } else if (action.folderName) {
          path = `${action.spaceName}/${action.folderName}`;
        } else {
          return; // Can't move-to-folder at space level
        }

        const itemId = await getSpaceIdByPath(path);
        if (!itemId) throw new Error(`Item not found for path: ${path}`);

        // Get space to find its folders
        const spaceId = await getSpaceIdByName(action.spaceName);
        if (!spaceId) throw new Error(`Space ${action.spaceName} not found`);

        const space = spaces.find(s => s.name === action.spaceName);
        if (!space) throw new Error(`Space ${action.spaceName} not found in local cache`);

        // Get available folders (excluding current folder if item is a list)
        const availableFolders = space.folders
          .filter(f => !action.folderName || f.name !== action.folderName)
          .map(f => f.name);

        if (availableFolders.length === 0) {
          toast('Nessuna cartella disponibile in questo spazio. Crea prima una cartella.', 'info');
          return;
        }

        const folderNames = availableFolders.join('\n');
        const targetFolderName = await dialogPrompt({ title: 'Sposta in cartella', message: `Sposta in quale cartella?\n\nCartelle disponibili:\n${folderNames}`, placeholder: 'Nome della cartella' });
        if (!targetFolderName || !targetFolderName.trim()) return;

        const targetFolderId = folderMappings?.[action.spaceName]?.[targetFolderName.trim()];
        if (!targetFolderId) throw new Error(`Folder ${targetFolderName} not found`);

        await moveSpace(itemId, targetFolderId);

        // Clear structure cache to force refresh
        try {
          await clearStructureCache();
        } catch (cacheError) {
          logger.error('Error clearing structure cache:', cacheError);
        }

        // Refresh spaces and categories structure
        try {
          await refreshAll();
        } catch (refreshError) {
          logger.error('Error refreshing spaces after move:', refreshError);
        }

        await refreshSpaceItems(action.spaceName);

        // Update filters if we moved the current item (ID-based path)
        const currentPath = filters.path;
        if (currentPath === path || currentPath?.startsWith(path + '/')) {
          const spaceObj = spaces.find(s => s.name === action.spaceName);
          const spaceId = spaceObj?.id || action.spaceName;
          setFilters({ path: `${spaceId}/${targetFolderId}/${itemId}` });
        }

        // Auto-expand the target folder
        // This will be handled by the useEffect in SpacesList that watches filters.path
      } catch (error) {
        logger.error('Error moving item to folder:', error);
        toast(
          `Errore nello spostamento: ${error instanceof Error ? error.message : 'Errore sconosciuto'}`,
          'error'
        );
      }
    } else if (action.type === 'archive') {
      // Archive item
      try {
        let itemId: string | null = null;
        let path = '';

        if (action.itemId) {
          itemId = action.itemId;
          path = action.spaceName;
        } else {
          if (action.listName) {
            path = action.folderName
              ? `${action.spaceName}/${action.folderName}/${action.listName}`
              : `${action.spaceName}/${action.listName}`;
          } else if (action.folderName) {
            path = `${action.spaceName}/${action.folderName}`;
          } else {
            path = action.spaceName;
          }

          itemId = await getSpaceIdByPath(path);
        }
        if (!itemId) throw new Error(`Item not found for path: ${path}`);

        const itemName = action.itemId
          ? (() => {
              const allItems = useAppStore.getState().projectItems;
              const spaceItems = allItems[action.spaceName] || [];
              return spaceItems.find(i => i.id === action.itemId)?.name || 'Elemento';
            })()
          : (action.listName || action.folderName || action.spaceName);
        const confirmed = await dialogConfirm({ title: 'Archivia', message: `Sei sicuro di voler archiviare "${itemName}"?`, confirmLabel: 'Archivia' });
        if (!confirmed) return;

        await archiveSpace(itemId);

        // Clear structure cache to remove archived space from cache
        try {
          await clearStructureCache();
        } catch (cacheError) {
          logger.error('Error clearing structure cache:', cacheError);
        }

        // Force refresh spaces and categories in store to remove archived space
        try {
          await refreshAll();
        } catch (refreshError) {
          logger.error('Error refreshing spaces after archiving:', refreshError);
        }

        await refreshSpaceItems(action.spaceName);

        // Clear filters if we archived the current item and redirect to projects view
        const currentPath = filters.path;
        if (currentPath === path || currentPath?.startsWith(path + '/')) {
          // Redirect to projects view instead of setting path to null
          setFilters({ path: null });
          // Change view mode to projects to avoid routing issues
          const setViewMode = useAppStore.getState().setViewMode;
          setViewMode('projects');
        }
      } catch (error) {
        logger.error('Error archiving item:', error);
        toast(
          `Errore nell'archiviazione: ${error instanceof Error ? error.message : 'Errore sconosciuto'}`,
          'error'
        );
      }
    } else if (action.type === 'duplicate') {
      // Duplicate item
      try {
        let itemId: string | null = null;

        if (action.itemId) {
          itemId = action.itemId;
        } else {
          let path = '';
          if (action.listName) {
            path = action.folderName
              ? `${action.spaceName}/${action.folderName}/${action.listName}`
              : `${action.spaceName}/${action.listName}`;
          } else if (action.folderName) {
            path = `${action.spaceName}/${action.folderName}`;
          } else {
            path = action.spaceName;
          }

          itemId = await getSpaceIdByPath(path);
        }
        if (!itemId) throw new Error(`Item not found`);

        const itemName = action.itemId
          ? (() => {
              const allItems = useAppStore.getState().projectItems;
              const spaceItems = allItems[action.spaceName] || [];
              return spaceItems.find(i => i.id === action.itemId)?.name || 'Elemento';
            })()
          : (action.listName || action.folderName || action.spaceName);
        const newName = await dialogPrompt({ title: 'Duplica', message: 'Nome della copia:', defaultValue: `${itemName} (copia)` });
        if (!newName || !newName.trim()) return;

        await duplicateSpace(itemId, newName.trim());
        await refreshSpaceItems(action.spaceName);
      } catch (error) {
        logger.error('Error duplicating item:', error);
        toast(
          `Errore nella duplicazione: ${error instanceof Error ? error.message : 'Errore sconosciuto'}`,
          'error'
        );
      }
    } else if (action.type.startsWith('add-')) {
      // Helper function to create items inline (without prompt)
      const createItemInline = async <T extends Document | Chat | VoiceChannel | Spreadsheet | Presentation>(
        type: ItemType,
        createFn: (spaceId: string, folderId: string | null, name: string) => Promise<T & { id: string }>,
        errorLabel: string
      ) => {
        try {
          // UNIFIED: Create inline without prompting for name
          // Build path and find containerId
          let containerId: string | null = null;

          // If folderName or listName looks like an ID (UUID format), use it directly
          const isUUID = (str: string) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(str);

          if (action.listName && isUUID(action.listName)) {
            containerId = action.listName;
          } else if (action.folderName && isUUID(action.folderName)) {
            containerId = action.folderName;
          } else {
            const containerPath = action.listName
              ? (action.folderName ? `${action.spaceName}/${action.folderName}/${action.listName}` : `${action.spaceName}/${action.listName}`)
              : (action.folderName ? `${action.spaceName}/${action.folderName}` : action.spaceName);

            containerId = await getSpaceIdByPath(containerPath);

            if (!containerId && action.folderName) {
              const spaceId = await getSpaceIdByName(action.spaceName);
              if (spaceId && folderMappings?.[action.spaceName]?.[action.folderName]) {
                containerId = folderMappings[action.spaceName][action.folderName];
              }
            }
          }

          if (!containerId) {
            const containerPath = action.listName
              ? (action.folderName ? `${action.spaceName}/${action.folderName}/${action.listName}` : `${action.spaceName}/${action.listName}`)
              : (action.folderName ? `${action.spaceName}/${action.folderName}` : action.spaceName);
            throw new Error(`Container not found for path: ${containerPath}`);
          }

          // UNIFIED: Create temporary item with empty name in editing mode (inline editing)
          const tempItemId = `temp-${Date.now()}`;

          // Optimistic update: add item immediately to UI with empty name
          addItemOptimistic({
            spaceName: action.spaceName,
            itemId: tempItemId,
            itemName: '', // Empty name - will be filled via inline editing
            itemType: type,
            parentId: containerId,
          });

          // Set item in editing mode
          setEditingItem(tempItemId, true);

          // Save creation info in store for later use when saving name
          setPendingItemCreation(tempItemId, {
            spaceName: action.spaceName,
            itemType: type as ItemType,
            containerId: containerId,
            folderId: null,
            folderName: action.folderName || null,
          });
        } catch (error) {
          logger.error(`Error creating ${errorLabel}:`, error);
          toast(
            `Errore nella creazione del ${errorLabel}: ${error instanceof Error ? error.message : 'Errore sconosciuto'}`,
            'error'
          );
        }
      };

      // Map action types to creation functions
      const itemCreators: Record<string, {
        type: ItemType;
        createFn: (spaceId: string, folderId: string | null, name: string) => Promise<(Document & { id: string }) | (Chat & { id: string }) | (VoiceChannel & { id: string }) | (Spreadsheet & { id: string }) | (Presentation & { id: string })>;
        errorLabel: string
      }> = {
        'add-document': {
          type: 'document',
          createFn: createDocument,
          errorLabel: 'documento'
        },
        'add-chat': {
          type: 'chat',
          createFn: createChat,
          errorLabel: 'chat'
        },
        'add-voice-channel': {
          type: 'voice_channel',
          createFn: createVoiceChannel,
          errorLabel: 'canale vocale'
        },
        'add-spreadsheet': {
          type: 'spreadsheet',
          createFn: createSpreadsheet,
          errorLabel: 'foglio di calcolo'
        },
        'add-presentation': {
          type: 'presentation',
          createFn: createPresentation,
          errorLabel: 'presentazione'
        },
      };

      const creator = itemCreators[action.type];
      if (creator) {
        await createItemInline(creator.type, creator.createFn, creator.errorLabel);
      }
    }
  }, [
    setSelectedProject,
    setSelectedFolder,
    setSelectedList,
    openSpaceSettings,
    openListSettings,
    setFilters,
    refreshSpaceItems,
    filters.path,
    removeSpaceItems,
    addItemOptimistic,
    folderMappings,
    setEditingItem,
    setPendingItemCreation,
    spaces,
    refreshAll,
    dialogConfirm,
    dialogPrompt,
  ]);

  // Export save handler for inline editing
  const savePendingItemName = useCallback(async (itemId: string, name: string) => {
    const pendingCreations = useAppStore.getState().pendingItemCreations;
    const creationInfo = pendingCreations[itemId];
    if (!creationInfo) {
      logger.error('No pending creation info found for item:', itemId);
      return;
    }

    const { spaceName, itemType, containerId, folderName: _folderName, categoryId } = creationInfo;

    // Handle category creation
    if (itemType === 'category') {
      setEditingItem(itemId, false);
      setPendingItemCreation(itemId, null);

      try {
        const { createCategory } = await import('../db/projects');
        await createCategory(name.trim(), currentOrgId);
        await refreshAll();
      } catch (error) {
        logger.error('Error creating category:', error);
        toast(
          `Errore nella creazione della categoria: ${error instanceof Error ? error.message : 'Errore sconosciuto'}`,
          'error'
        );
        // Rollback - remove temp category from categories
        const currentCategories = useAppStore.getState().categories;
        useAppStore.getState().setCategories(currentCategories.filter(c => c.id !== itemId));
      }
      return;
    }

    // Handle space creation
    if (containerId === 'root' || !itemType || itemType === ('space' as ItemType)) {
      // This is a new space
      setEditingItem(itemId, false);
      setPendingItemCreation(itemId, null);

      // Create space in database
      try {
        const { createProject: createSpace } = await import('../db/projects');
        const createdSpace = await createSpace(name.trim(), undefined, currentOrgId, categoryId || undefined);

        if (!createdSpace) {
          throw new Error('Failed to create space');
        }

        // Refresh spaces and categories structure
        const refreshedSpaces = await refreshAll();

        // Navigate to new space using its ID
        const createdSpaceInStore = refreshedSpaces.find(s => s.name === name.trim());
        if (createdSpaceInStore) {
          setFilters({ path: createdSpaceInStore.id }, { skipCloseItems: true });
        }
      } catch (error) {
        logger.error('Error creating space:', error);
        // Show error to user
        toast(
          `Errore nella creazione del progetto: ${error instanceof Error ? error.message : 'Errore sconosciuto'}`,
          'error'
        );
        // Rollback - remove temp space
        const currentSpaces = useAppStore.getState().spaces;
        const updatedSpaces = currentSpaces.filter(s => s.id !== itemId);
        useAppStore.getState().setSpaces(updatedSpaces);
        return;
      }
      return;
    }

    // Handle other item types (document, chat, etc.)
    const itemTypes: ItemType[] = ['document', 'chat', 'voice_channel', 'spreadsheet', 'presentation'];
    if (itemTypes.includes(itemType as ItemType)) {
      // DON'T clear editing state yet — keep temp item visible in sidebar
      // while creating in DB to avoid flash/disappearance

      try {
        // Use the space ID for navigation (containerId is the space that holds this item)
        const spaceForNav = spaces.find(s => s.name === spaceName);
        const navigationPath = spaceForNav?.id || spaceName;
        setFilters({ path: navigationPath }, { skipCloseItems: true });

        // Create item based on type with orderIndex: 0 to place at top
        let createdItem: Document | Chat | VoiceChannel | Spreadsheet | Presentation | null = null;
        if (itemType === 'document') {
          createdItem = await createDocument(containerId, null, name.trim(), 0);
        } else if (itemType === 'chat') {
          createdItem = await createChat(containerId, null, name.trim(), 0);
        } else if (itemType === 'voice_channel') {
          createdItem = await createVoiceChannel(containerId, null, name.trim(), 0);
        } else if (itemType === 'spreadsheet') {
          createdItem = await createSpreadsheet(containerId, null, name.trim(), 0);
        } else if (itemType === 'presentation') {
          createdItem = await createPresentation(containerId, null, name.trim(), 0);
        }

        if (!createdItem) {
          throw new Error(`Failed to create ${itemType}`);
        }

        // Replace temp item with real item
        const current = useAppStore.getState().projectItems;
        const spaceItems = current[spaceName] || [];
        const updatedItems = spaceItems.map(i => i.id === itemId ? createdItem : i);
        useAppStore.getState().updateSpaceItems(spaceName, updatedItems);

        // NOW clear editing state — real item is in both projectItems and DB
        setEditingItem(itemId, false);
        setPendingItemCreation(itemId, null);

        // Set current item
        setCurrentItem(itemType as ItemType, createdItem, navigationPath);

        // Refresh space items in background
        refreshSpaceItems(spaceName).catch((error) => logger.error('Error refreshing space items:', error));
      } catch (error) {
        logger.error(`Error creating ${itemType}:`, error);
        // Clear editing state on error
        setEditingItem(itemId, false);
        setPendingItemCreation(itemId, null);
        // Show error to user
        toast(
          `Errore nella creazione: ${error instanceof Error ? error.message : 'Errore sconosciuto'}`,
          'error'
        );
        // Rollback - remove temp item
        const current = useAppStore.getState().projectItems;
        const spaceItems = current[spaceName] || [];
        useAppStore.getState().updateSpaceItems(
          spaceName,
          spaceItems.filter(i => i.id !== itemId)
        );
        return;
      }
      return;
    }

    // Handle folders and lists
    const currentSpaces = useAppStore.getState().spaces;
    if (itemType === 'folder') {
      const updatedSpaces = currentSpaces.map(space => {
        if (space.name !== spaceName) return space;
        return {
          ...space,
          folders: space.folders.map(f => f.id === itemId ? { ...f, name: name.trim() } : f),
        };
      });
      useAppStore.getState().setSpaces(updatedSpaces);
      updateFolderMappingOptimistic(spaceName, name.trim(), itemId);
    } else if (itemType === 'list') {
      const updatedSpaces = currentSpaces.map(space => {
        if (space.name !== spaceName) return space;
        return {
          ...space,
          folders: space.folders.map(folder => ({
            ...folder,
            lists: folder.lists.map(l => l.id === itemId ? { ...l, name: name.trim() } : l),
          })),
        };
      });
      useAppStore.getState().setSpaces(updatedSpaces);
    }

    setEditingItem(itemId, false);
    setPendingItemCreation(itemId, null);

    try {
      // Create item first so we have the real ID for navigation
      const createdItem = itemType === 'folder'
        ? await createFolder(containerId, name.trim(), 0) // orderIndex: 0 to insert at top
        : await createList(containerId, name.trim(), 0); // orderIndex: 0 to insert at top

      if (!createdItem) {
        throw new Error(`Failed to create ${itemType === 'folder' ? 'folder' : 'list'}`);
      }

      // Update with real item ID for folders
      if (itemType === 'folder') {
        updateFolderMappingOptimistic(spaceName, name.trim(), createdItem.id);
      }

      // Build ID-based path to the newly created item
      // Find the space ID from the spaces array
      const space = spaces.find(s => s.name === spaceName);
      const spaceId = space?.id || spaceName;
      const folderId = creationInfo.folderId;
      let itemPath: string;
      if (itemType === 'folder') {
        itemPath = `${spaceId}/${createdItem.id}`;
      } else if (folderId) {
        itemPath = `${spaceId}/${folderId}/${createdItem.id}`;
      } else {
        itemPath = `${spaceId}/${createdItem.id}`;
      }

      // Navigate to and select the newly created item
      setFilters({ path: itemPath }, { skipCloseItems: true });

      // Refresh spaces and categories structure in background to sync with server
      try {
        await refreshAll();
      } catch (refreshError) {
        logger.error(`Error refreshing spaces after ${itemType} creation:`, refreshError);
      }

      // Refresh space items
      refreshSpaceItems(spaceName).catch((error) => logger.error('Error refreshing space items:', error));
    } catch (error) {
      logger.error(`Error creating ${itemType}:`, error);
      // Show error to user
      toast(
        `Errore nella creazione: ${error instanceof Error ? error.message : 'Errore sconosciuto'}`,
        'error'
      );
      // Rollback on error
      await refreshAll();
    }
  }, [setEditingItem, setPendingItemCreation, setFilters, updateFolderMappingOptimistic, refreshSpaceItems, setCurrentItem, refreshAll, currentOrgId, spaces]);

  // Override store's savePendingItemName with our implementation
  useEffect(() => {
    useAppStore.setState({ savePendingItemName });
  }, [savePendingItemName]);

  return {
    handleContextMenuAction,
    savePendingItemName,
  };
}

